import { useState } from 'react';
import { useTr } from '../utils/useTr';
import { getReportReasons, fileReport, blockUser } from '../utils/safety';
import { useApp } from '../contexts/AppContext';

// ── Report + Block sheet ──────────────────────────────────────────────────
export function ReportBlockSheet({ user, onClose, onBlocked }) {
  const tr = useTr();
  const { currentUser , lang} = useApp();
  const [step, setStep]       = useState('menu');    // menu | report | confirm | done
  const [reason, setReason]   = useState('');
  const [details, setDetails] = useState('');
  const [action, setAction]   = useState('');        // 'report' | 'block' | 'both'

  const handleBlock = () => {
    blockUser(currentUser.id, user.id);
    if (onBlocked) onBlocked(user.id);
    setStep('done');
    setAction('block');
  };

  const handleReport = () => {
    if (!reason) return;
    fileReport(currentUser.id, user.id, reason, details);
    if (action === 'both') blockUser(currentUser.id, user.id);
    if (action === 'both' && onBlocked) onBlocked(user.id);
    setStep('done');
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:900, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(6px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', borderRadius:'var(--r-xl) var(--r-xl) 0 0', maxWidth:480, width:'100%', overflow:'hidden' }}>

        {/* ── Menu ── */}
        {step === 'menu' && (<>
          <div style={{ padding:'1rem 1.2rem 0.5rem', textAlign:'center' }}>
            <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 1rem' }}/>
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', justifyContent:'center', marginBottom:'0.3rem' }}>
              <div style={{ width:36, height:36, borderRadius:'50%', overflow:'hidden', border:'1px solid var(--border)' }}>
                {(user.photos||'')[0] ? <img src={user.photos[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <div style={{ width:'100%', height:'100%', background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--muted)', fontSize:'0.9rem' }}>{(user.name||'')[0]}</div>}
              </div>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--white)' }}>{user.name}</span>
            </div>
          </div>
          <div style={{ padding:'0.5rem 1.2rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            {[
              { label: `Report ${user.name?.split(' ')[0]}`, icon: '⚑', sub: tr('report.report_sub'), fn: () => { setAction('report'); setStep('report'); }, color: 'var(--white)' },
              { label: 'Block', icon: '✕', sub: tr('report.block_note'), fn: () => setStep('confirm'), color: '#E07070' },
              { label: tr('report.both_cta'), icon: '⚑', sub: tr('report.then_block'), fn: () => { setAction('both'); setStep('report'); }, color: '#E07070' },
            ].map(item => (
              <button key={item.label} onClick={item.fn} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.85rem 1rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', cursor:'pointer', textAlign:'left', width:'100%' }}>
                <span style={{ fontSize:'1.1rem', flexShrink:0 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize:'0.82rem', fontWeight:600, color:item.color, fontFamily:'Outfit' }}>{item.label}</div>
                  <div style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.08rem' }}>{item.sub}</div>
                </div>
              </button>
            ))}
            <button onClick={onClose} style={{ padding:'0.65rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', cursor:'pointer', fontFamily:'Outfit', fontSize:'0.75rem', marginTop:'0.2rem' }}>{tr('common.cancel')}</button>
          </div>
        </>)}

        {/* ── Report form ── */}
        {step === 'report' && (<>
          <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'0.7rem' }}>
            <button onClick={()=>setStep('menu')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)', padding:'0.2rem' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--white)' }}>Report {user.name?.split(' ')[0]}</span>
          </div>
          <div style={{ padding:'1rem 1.2rem', display:'flex', flexDirection:'column', gap:'0.45rem', maxHeight:'55vh', overflowY:'auto' }}>
            <p style={{ fontSize:'0.72rem', color:'var(--muted)', marginBottom:'0.3rem', lineHeight:1.5 }}>{tr('report.whats_going')}</p>
            {getReportReasons(lang).map(r => (
              <button key={r.id} onClick={() => setReason(r.id)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 0.9rem', background: reason===r.id ? 'var(--gold-pale)' : 'var(--dark)', border:`1px solid ${reason===r.id ? 'var(--gold)' : 'var(--border)'}`, borderRadius:'var(--r-md)', cursor:'pointer', textAlign:'left' }}>
                <span style={{ fontSize:'0.8rem', color: reason===r.id ? 'var(--gold)' : 'var(--white)', fontFamily:'Outfit' }}>{r.label}</span>
                {r.severity === 'critical' && <span style={{ fontSize:'0.55rem', background:'rgba(224,112,112,0.15)', color:'#E07070', padding:'0.05rem 0.35rem', borderRadius:99, fontFamily:'Outfit', fontWeight:600 }}>{tr('common.urgent')}</span>}
              </button>
            ))}
            {reason && (
              <textarea placeholder={tr('report.details_ph')} value={details} onChange={e=>setDetails(e.target.value)} rows={3} style={{ marginTop:'0.4rem', width:'100%', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.6rem 0.8rem', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.78rem', resize:'none', lineHeight:1.5, boxSizing:'border-box' }}/>
            )}
          </div>
          <div style={{ padding:'0.8rem 1.2rem 1.5rem' }}>
            <button onClick={handleReport} disabled={!reason} style={{ width:'100%', padding:'0.75rem', background: reason ? '#E07070' : 'var(--dark)', border:`1px solid ${reason ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color: reason ? '#fff' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:700, cursor: reason ? 'pointer' : 'default', letterSpacing:'0.06em' }}>
              Submit report{action==='both' ? ' & block' : ''}
            </button>
          </div>
        </>)}

        {/* ── Block confirm ── */}
        {step === 'confirm' && (<>
          <div style={{ padding:'1.5rem 1.2rem', textAlign:'center' }}>
            <div style={{ fontSize:'2.5rem', marginBottom:'0.8rem' }}>✕</div>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', color:'var(--white)', marginBottom:'0.4rem' }}>Block {user.name?.split(' ')[0]}?</h3>
            <p style={{ fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.6, marginBottom:'1.2rem' }}>They won't be able to see your profile, send you messages, or appear in your Discover feed. You can unblock them from your profile settings.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              <button onClick={handleBlock} style={{ padding:'0.75rem', background:'rgba(224,112,112,0.12)', border:'1px solid rgba(224,112,112,0.35)', borderRadius:'var(--r-md)', color:'#E07070', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>Yes, block {user.name?.split(' ')[0]}</button>
              <button onClick={onClose} style={{ padding:'0.65rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.75rem', cursor:'pointer' }}>{tr('common.cancel')}</button>
            </div>
          </div>
        </>)}

        {/* ── Done ── */}
        {step === 'done' && (<>
          <div style={{ padding:'2rem 1.2rem', textAlign:'center' }}>
            <div style={{ fontSize:'2.5rem', marginBottom:'0.8rem' }}>✅</div>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', color:'var(--white)', marginBottom:'0.4rem' }}>
              {action === 'block' ? 'Blocked' : action === 'both' ? 'Reported & blocked' : tr('report.done_reported')}
            </h3>
            <p style={{ fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.6, marginBottom:'1.2rem' }}>
              {action === 'block'
                ? `${user.name?.split(' ')[0]} has been blocked. You won't see each other anymore.`
                : 'Thank you for helping keep BeeLove safe. Our team reviews all reports within 24 hours.'}
            </p>
            <button onClick={onClose} style={{ padding:'0.7rem 2rem', background:'var(--gold)', border:'none', borderRadius:'var(--r-md)', color:'var(--black)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>{tr('common.done')}</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ── Small report button to embed in cards/profiles ────────────────────────
export function ReportButton({ user, style }) {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const { setCurrentUser } = useApp();

  return (<>
    <button onClick={e=>{e.stopPropagation();setOpen(true);}} title={tr('report.title')}
      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)', padding:'0.3rem', display:'flex', alignItems:'center', ...style }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </button>
    {open && (
      <ReportBlockSheet user={user} onClose={()=>setOpen(false)}
        onBlocked={()=>setOpen(false)}/>
    )}
  </>);
}
